import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../permissions/PermissionContext';
import {
  MENU_MODULE_MAP,
  MENU_GROUP_CHILDREN,
  isOwnerAdminOnlyMenu,
} from '../permissions/menuModuleMap';
import { pathForMenu } from '../routes/menuPaths';
import { Spinner } from './ui/Spinner';
import AccessRestricted from './AccessRestricted';

const FALLBACK_MENU_ORDER = [
  'Dashboard',
  'Orders',
  'Stock',
  'Challans',
  'Products',
  'Designs',
  'Feeders',
  'Colors',
  'Production Units',
  'Categories',
  'Sub Categories',
  'Concepts',
  'Settings',
];

export function firstAllowedPath(canViewMenu, ownerAdmin) {
  if (ownerAdmin) {
    return '/';
  }
  for (const menuId of FALLBACK_MENU_ORDER) {
    if (canViewMenu(menuId)) {
      return pathForMenu(menuId);
    }
  }
  for (const children of Object.values(MENU_GROUP_CHILDREN)) {
    for (const childId of children) {
      if (canViewMenu(childId)) {
        return pathForMenu(childId);
      }
    }
  }
  return null;
}

/**
 * @param {{ menuId?: string, module?: string, action?: string, ownerAdminOnly?: boolean, children: React.ReactNode }} props
 */
function ProtectedRoute({ menuId, module, action = 'VIEW', ownerAdminOnly = false, children }) {
  const { loading, ownerAdmin, accessRestricted, can, canViewMenu } = usePermissions();

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (accessRestricted) {
    return <AccessRestricted />;
  }

  if (ownerAdminOnly) {
    if (!ownerAdmin) {
      const fallback = firstAllowedPath(canViewMenu, false);
      if (fallback) {
        return <Navigate to={fallback} replace />;
      }
      return <AccessRestricted />;
    }
    return children;
  }

  if (ownerAdmin) {
    return children;
  }

  const resolvedMenuId = menuId;
  if (resolvedMenuId && isOwnerAdminOnlyMenu(resolvedMenuId)) {
    const fallback = firstAllowedPath(canViewMenu, false);
    if (fallback) {
      return <Navigate to={fallback} replace />;
    }
    return <AccessRestricted />;
  }

  const moduleKey = module || (resolvedMenuId ? MENU_MODULE_MAP[resolvedMenuId] : null);
  if (!moduleKey) {
    const fallback = firstAllowedPath(canViewMenu, false);
    if (fallback) {
      return <Navigate to={fallback} replace />;
    }
    return <AccessRestricted />;
  }

  if (!can(moduleKey, action)) {
    const fallback = firstAllowedPath(canViewMenu, false);
    if (fallback && fallback !== window.location.pathname) {
      return <Navigate to={fallback} replace />;
    }
    return <AccessRestricted />;
  }

  return children;
}

export default ProtectedRoute;
